<template>
    <span
        class="change-zip-section"
        @keydown="handleKeydown">
        <button
            v-show="!ShowZipCode"
            ref="changeLocationBtn"
            :aria-expanded="ariaExpanded"
            aria-controls="changelocation"
            class="change-location"
            @click="changeZipCode">Change Location</button>
        <div
            v-show="ShowZipCode"
            id="changelocation"
            :class="{ 'tab-focus-loop': isTabFocusLoop, 'negative-margin': true }">
            <label :class="{ 'active-float-labels-yantr': isFloatLabelsActive, 'zip-code-input-container': true }">
                <span class="zip-label">ZIP Code</span>
                <div class="zip-input">
                    <i class="lt4-MapPin"></i>
                    <zip-code-input
                        ref="geoInput"
                        v-model="ZipCode"
                        :autofocus="true"
                        :aria-invalid="ariaInvalid"
                        :class="{ 'label-float': isLabelFloat }"
                        @input="getLocationByZip"
                        @enter="submitZip"
                        @keyup="onInputKeyup"
                        @blur="onInputBlur"
                    />
                </div>
            </label>
            <button
                type="submit"
                class="button-confirm"
                :disabled="!IsZipCodeValid"
                @click="submitZip">Confirm</button>
            <button
                ref="cancelBtn"
                type="reset"
                class="button-cancel"
                @click="hideZipCode(true)">Cancel</button>
        </div>
        <span
            v-show="ShowZipCodeError"
            id="zipCodeError"
            class="zip-error">{{ errorMessage }}</span>
    </span>
</template>

<script>
    import ZipCodeInput from './zip-code-input.vue';
    import GetLocation from '~/mixin/get-geo-location';
    import StringFormat from '~/helpers/string-format';

    export default {
        components: {
            ZipCodeInput,
        },
        mixins: [GetLocation],
        props: {
            showZipCode: {
                type: Boolean,
                default: false,
            },
            zipCode: {
                type: String,
                default: '',
            },
            locationData: {
                type: [Object, String],
                default: '',
            },
            isZipCodeValid: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                isTabFocusLoop: false,
                isFloatLabelsActive: false,
                isLabelFloat: false,
                ariaInvalid: 'false',
                ShowZipCode: this.showZipCode,
                ZipCode: this.zipCode,
                InitialiZipCode: this.initialiZipCode,
                LocationData: this.locationData,
                IsZipCodeValid: this.isZipCodeValid,
                ShowZipCodeError: false,
                errorMessage: 'Please enter a valid zip code.',
            };
        },
        computed: {
            ariaExpanded() {
                return this.ShowZipCode ? 'true' : 'false';
            },
        },
        watch: {
            zipCode(val) {
                this.ZipCode = val;
            },
            showZipCode(val) {
                this.ShowZipCode = val;
            },
        },
        methods: {
            changeZipCode() {
                this.ZipCode = '';
                this.IsZipCodeValid = false;
                this.ShowZipCodeError = false;
                this.ShowZipCode = true;
                this.isFloatLabelsActive = true;
                this.isTabFocusLoop = true;
                this.$emit('showHideLocation');
                this.$nextTick(() => {
                    this.$refs.geoInput?.$el?.focus();
                });
            },
            hideZipCode(focusChangeBtn) {
                this.ShowZipCode = false;
                this.ShowZipCodeError = false;
                this.isTabFocusLoop = false;
                this.ariaInvalid = 'false';
                this.$emit('showHideLocation');
                if (focusChangeBtn) {
                    this.$nextTick(() => {
                        this.$refs.changeLocationBtn?.focus();
                    });
                }
            },
            submitZip() {
                if (this.LocationData && this.IsZipCodeValid) {
                    this.updateLocation(this.LocationData);
                    this.$nextTick(() => {
                        this.$refs.changeLocationBtn?.focus();
                    });
                } else {
                    this.IsZipCodeValid = false;
                }
            },
            updateLocation(val) {
                val.city = StringFormat.capitalize(val.city);
                this.$emit('input', val);
                this.initialiZipCode = val.zipCode;
                this.hideZipCode(false);
                this.ZipCode = '';
            },
            defaultLocationByIp() {
                GetLocation.methods.GetIpAddress(this.updateLocation);
            },
            getLocationByZip() {
                if (this.zipCodeValidator()) {
                    GetLocation.methods.GetZipCode(this.ZipCode, this.checkLocationResponse);
                } else {
                    this.IsZipCodeValid = false;
                    this.ShowZipCodeError = this.IsZipCodeValid;
                }
            },
            zipCodeValidator() {
                const zipCodeRegEx = /^\d{5}(?:[-\s]\d{4})?$/;
                if (this.ZipCode === '') {
                    this.ariaInvalid = 'false';
                }
                return zipCodeRegEx.test(this.ZipCode) && this.ZipCode !== '';
            },
            checkLocationResponse(val) {
                if (val.city) {
                    this.LocationData = val;
                    this.IsZipCodeValid = true;
                    this.ShowZipCodeError = false;
                    this.ariaInvalid = 'false';
                } else {
                    this.IsZipCodeValid = false;
                    this.ShowZipCodeError = true;
                    this.ariaInvalid = 'true';
                }
            },
            onInputKeyup() {
                if (this.ZipCode.length > 0) {
                    this.isLabelFloat = true;
                }
            },
            onInputBlur() {
                if (this.ZipCode.length === 0) {
                    this.isLabelFloat = false;
                }
            },
            handleKeydown(event) {
                if (event.key !== 'Tab' || !this.isTabFocusLoop) {
                    return;
                }
                const geoInputEl = this.$refs.geoInput?.$el;
                const cancelBtnEl = this.$refs.cancelBtn;
                if (!geoInputEl || !cancelBtnEl) {
                    return;
                }
                if (event.shiftKey && document.activeElement === geoInputEl) {
                    event.preventDefault();
                    cancelBtnEl.focus();
                } else if (!event.shiftKey && document.activeElement === cancelBtnEl) {
                    event.preventDefault();
                    geoInputEl.focus();
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
@import "../../assets/styles/grove/buttons";
@import "../../assets/styles/grove/inputs";
@import "../../assets/styles/grove/typographic";

.change-location {
    color: $lt-blue;
    font-weight: normal;
    font-size: 12px;
    text-decoration: underline;
    display: inline-block;
    margin-left: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all 0.3s;

    &:focus {
        outline: 1px auto -webkit-focus-ring-color;
        outline-offset: 1px;
    }
}

.button-confirm {
    @include button-common;
    @include button-size-medium;
    @include button-primary-blue;
    color: $white !important;
}
.button-cancel {
    @include button-common;
    @include button-size-medium;
    @include button-primary-salmon;
    color: $white !important;
}

.zip-code-input-container {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .zip-label {
        color: $lt-gray-dark-4;
        font-family: $lt-font-family-primary;
        font-size: 16px;
        font-weight: normal !important;
        line-height: 1.5;
    }
    .zip-input {
        @include input;
        @include input-size-small;
        @include input-standard-text;

        .lt4-MapPin {
            color: $black !important;
        }

        input {
            all: unset !important;
            font-weight: normal !important;
            color: $lt-blue-deep !important;
            padding-left: 8px !important;
        }
    }
}

.negative-margin {
    margin-bottom: -10px;
}

.zip-error {
    color: $lt-red;
    font-family: $lt-font-family-primary;
    font-size: 12px;
    line-height: 1;
}
</style>
